import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { startOfDay, endOfDay, subDays, subMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';

export type DatePreset = 'today' | 'yesterday' | 'last7days' | 'last30days' | 'thisWeek' | 'thisMonth' | 'lastMonth' | 'allTime' | 'custom';

export interface DateRange {
  from: Date;
  to: Date;
}

interface DateFilterContextType {
  preset: DatePreset;
  dateRange: DateRange;
  setPreset: (preset: DatePreset) => void;
  setCustomRange: (from: Date, to: Date) => void;
  resetToToday: () => void;
  isInRange: (date: Date | undefined | null) => boolean;
  getPresetLabel: () => string;
}

const DateFilterContext = createContext<DateFilterContextType | undefined>(undefined);

const STORAGE_KEY = 'medius360_date_filter';

function getDateRangeForPreset(preset: DatePreset, customFrom?: Date, customTo?: Date): DateRange {
  const now = new Date();
  
  switch (preset) {
    case 'today':
      return { from: startOfDay(now), to: endOfDay(now) };
    case 'yesterday':
      const yesterday = subDays(now, 1);
      return { from: startOfDay(yesterday), to: endOfDay(yesterday) };
    case 'last7days':
      return { from: startOfDay(subDays(now, 6)), to: endOfDay(now) };
    case 'last30days':
      return { from: startOfDay(subDays(now, 29)), to: endOfDay(now) };
    case 'thisWeek':
      return { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfWeek(now, { weekStartsOn: 1 }) };
    case 'thisMonth':
      return { from: startOfMonth(now), to: endOfMonth(now) };
    case 'lastMonth': {
      const lm = subMonths(now, 1);
      return { from: startOfMonth(lm), to: endOfMonth(lm) };
    }
    case 'allTime':
      return { from: new Date(2020, 0, 1), to: endOfDay(now) };
    case 'custom':
      return { 
        from: customFrom ? startOfDay(customFrom) : startOfDay(now), 
        to: customTo ? endOfDay(customTo) : endOfDay(now) 
      };
    default:
      return { from: startOfDay(now), to: endOfDay(now) };
  }
}

export function DateFilterProvider({ children }: { children: ReactNode }) {
  const [preset, setPresetState] = useState<DatePreset>('today');
  const [customFrom, setCustomFrom] = useState<Date | undefined>();
  const [customTo, setCustomTo] = useState<Date | undefined>();

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.preset) {
          setPresetState(parsed.preset);
          if (parsed.preset === 'custom' && parsed.customFrom && parsed.customTo) {
            setCustomFrom(new Date(parsed.customFrom));
            setCustomTo(new Date(parsed.customTo));
          }
        }
      } catch (e) {
        // Invalid stored data, use default
      }
    }
  }, []);

  // Save to localStorage when changed
  useEffect(() => {
    const data = {
      preset,
      customFrom: customFrom?.toISOString(),
      customTo: customTo?.toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [preset, customFrom, customTo]);

  const dateRange = getDateRangeForPreset(preset, customFrom, customTo);

  const setPreset = (newPreset: DatePreset) => {
    setPresetState(newPreset);
    if (newPreset !== 'custom') {
      setCustomFrom(undefined);
      setCustomTo(undefined);
    }
  };

  const setCustomRange = (from: Date, to: Date) => {
    setCustomFrom(from);
    setCustomTo(to);
    setPresetState('custom');
  };

  const resetToToday = () => {
    setPreset('today');
  };

  const isInRange = (date: Date | undefined | null): boolean => {
    if (!date) return false;
    const d = new Date(date);
    return d >= dateRange.from && d <= dateRange.to;
  };

  const getPresetLabel = (): string => {
    switch (preset) {
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case 'last7days': return 'Last 7 Days';
      case 'last30days': return 'Last 30 Days';
      case 'thisWeek': return 'This Week';
      case 'thisMonth': return 'This Month';
      case 'lastMonth': return 'Last Month';
      case 'allTime': return 'All Time';
      case 'custom':
        if (customFrom && customTo) {
          const formatDate = (d: Date) => d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
          return `${formatDate(customFrom)} – ${formatDate(customTo)}`;
        }
        return 'Custom';
      default: return 'Today';
    }
  };

  return (
    <DateFilterContext.Provider value={{
      preset,
      dateRange,
      setPreset,
      setCustomRange,
      resetToToday,
      isInRange,
      getPresetLabel,
    }}>
      {children}
    </DateFilterContext.Provider>
  );
}

export function useDateFilter() {
  const context = useContext(DateFilterContext);
  if (context === undefined) {
    throw new Error('useDateFilter must be used within a DateFilterProvider');
  }
  return context;
}
